import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useCms } from "@/lib/cms";
import { BACKEND_BASE } from "@/lib/backend";
import CountUp from "@/components/CountUp";
import ResponsivePreviewSection from "@/components/ResponsivePreviewSection";
import HeroAiConsultBar from "@/components/sections/hero/HeroAiConsultBar";
import { handleContactClick } from "@/lib/contactForm";
import {
  HeroBlock,
  HERO_STATS,
  getHeroStatCountUpProps,
  formatPreviewStat,
  parseTitleLines,
  parseHeroSubtitle,
} from "@/components/sections/hero/heroShared";

const absUrl = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND_BASE}${u}`) : "");

function toRouterPath(href) {
  if (!href) return "/";
  if (href.startsWith("#")) return `/${href}`;
  return href;
}

function isInternalHref(href) {
  return href.startsWith("/") || href.startsWith("#");
}

/**
 * Main hero left column copy — badge, title, subtitle, CTAs, stats.
 * Hidden on landing when HERO_LEFT_COPY_ENABLED is false; shown on /about via SavedHeroLeftCopySection.
 */
export default function HeroLeftCopy({
  embedMode = false,
  showDevicePreview = false,
  showAiBar = false,
  showScroll = false,
  isInlinePreview = false,
  revealOnScroll = false,
  useRouterLinks = false,
  titleAs = "h1",
}) {
  const { cms } = useCms();

  const highlight = (cms.hero_title_highlight || "성장").trim();
  const { titleLine1, titleLine2Before, titleLine2Highlight, titleLine2After } = parseTitleLines(
    cms.hero_title,
    highlight,
  );
  const titleLine2 =
    titleLine2Before || titleLine2Highlight || titleLine2After
      ? `${titleLine2Before}${titleLine2Highlight}${titleLine2After}`
      : "";
  const { lead: subtitleLead, body: subtitleBody } = parseHeroSubtitle(cms.hero_subtitle);

  const primaryHref = cms.cta_primary_link || "#contact";
  const secondaryHref = cms.cta_secondary_link || "/about#services";
  const primaryText = cms.cta_primary_text || "무료 상담 신청";
  const secondaryText = cms.cta_secondary_text || "서비스 살펴보기";

  const blockProps = { embedMode, revealOnScroll };

  const PrimaryCta = useRouterLinks && isInternalHref(primaryHref) ? Link : "a";
  const SecondaryCta = useRouterLinks && isInternalHref(secondaryHref) ? Link : "a";
  const primaryCtaProps =
    PrimaryCta === Link ? { to: toRouterPath(primaryHref) } : { href: primaryHref };
  const secondaryCtaProps =
    SecondaryCta === Link ? { to: toRouterPath(secondaryHref) } : { href: secondaryHref };

  const onPrimaryClick = (e) => {
    handleContactClick(e, primaryHref);
  };

  return (
    <>
      <HeroBlock
        {...blockProps}
        motionConfig={{
          initial: { opacity: 0, y: 20 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.6, delay: 0.1 },
        }}
        data-testid="hero-badge"
        className="ds-hero-badge ds-hero-glass ds-hero-clickable"
      >
        {absUrl(cms.media_hero_badge_icon) ? (
          <img
            src={absUrl(cms.media_hero_badge_icon)}
            alt=""
            data-testid="hero-badge-icon"
            className="h-4 w-4 object-contain"
          />
        ) : (
          <span className="ds-hero-badge-mark" aria-hidden="true">
            D
          </span>
        )}
        <span>{cms.hero_badge_text || "DSITESOLUTION"}</span>
        <span className="pulse-dot" />
      </HeroBlock>

      <HeroBlock
        {...blockProps}
        as={titleAs}
        motionConfig={{
          initial: { opacity: 0, y: 24 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.7, delay: 0.2 },
        }}
        data-testid="hero-title"
        className="ds-hero-title ds-hero-clickable ds-cms-text"
      >
        <span className="ds-hero-title-line">{titleLine1}</span>
        {titleLine2 ? (
          <span className="ds-hero-title-line ds-hero-title-line--sub">
            {titleLine2Before}
            {titleLine2Highlight ? (
              <span data-testid="hero-title-highlight" className="ds-hero-title-accent">
                {titleLine2Highlight}
              </span>
            ) : null}
            {titleLine2After}
          </span>
        ) : null}
      </HeroBlock>

      {showDevicePreview && !embedMode ? (
        <HeroBlock
          {...blockProps}
          motionConfig={{
            initial: { opacity: 0, y: 16 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.65, delay: 0.35 },
          }}
          className="ds-hero-preview-wrap ds-hero-clickable"
        >
          <ResponsivePreviewSection variant="hero-inline" />
        </HeroBlock>
      ) : null}

      <HeroBlock
        {...blockProps}
        as="p"
        motionConfig={{
          initial: { opacity: 0, y: 24 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.7, delay: 0.5 },
        }}
        data-testid="hero-subtitle"
        className="ds-hero-subtitle ds-hero-clickable ds-cms-text"
      >
        {subtitleLead ? <span className="ds-hero-subtitle-lead">{subtitleLead}</span> : null}
        {subtitleBody ? (
          <span className="ds-hero-subtitle-body whitespace-pre-line">{subtitleBody}</span>
        ) : null}
      </HeroBlock>

      <HeroBlock
        {...blockProps}
        motionConfig={{
          initial: { opacity: 0, y: 24 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.7, delay: 0.58 },
        }}
        className="ds-hero-cta-row ds-hero-clickable"
      >
        <PrimaryCta
          {...primaryCtaProps}
          data-testid="hero-primary-cta"
          className="ds-hero-btn-primary"
          onClick={onPrimaryClick}
        >
          {primaryText}
          <ArrowRight size={16} />
        </PrimaryCta>
        <SecondaryCta
          {...secondaryCtaProps}
          data-testid="hero-secondary-cta"
          className="ds-hero-btn-secondary ds-hero-glass"
        >
          {secondaryText}
        </SecondaryCta>
      </HeroBlock>

      <HeroBlock
        {...blockProps}
        motionConfig={{
          initial: { opacity: 0, y: 24 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.7, delay: 0.65 },
        }}
        className="ds-hero-stats ds-hero-clickable"
      >
        {HERO_STATS.map((stat, index) => (
          <div key={stat.testId} className="ds-hero-stat-item" data-stat-id={stat.testId}>
            <div className={`ds-hero-stat-value${stat.accent ? " is-accent" : ""}`}>
              {isInlinePreview ? (
                <span data-testid={stat.testId}>{formatPreviewStat(stat)}</span>
              ) : (
                <CountUp {...getHeroStatCountUpProps(stat, index)} />
              )}
            </div>
            <div className="ds-hero-stat-label">{stat.label}</div>
          </div>
        ))}
      </HeroBlock>

      {showAiBar && !embedMode ? (
        <HeroAiConsultBar subMessage="프리미엄 서비스 섹션에서 각 솔루션 카드를 확인하고 상담을 요청해 보세요." />
      ) : null}

      {showScroll && showDevicePreview && !embedMode ? (
        <div className="ds-hero-scroll ds-hero-scroll--pinned" aria-hidden="true" data-testid="scroll-down">
          <span className="ds-hero-scroll-line" />
          <span className="ds-hero-scroll-text">
            SCROLL
            <br />
            DOWN
          </span>
        </div>
      ) : null}
    </>
  );
}
